import type { ThemeConfig } from 'antd';
import { theme as antdTheme } from 'antd';
import { palettes, type ColorScheme } from './palette';

/** Ant Design theme derived from the kit tokens for one colour scheme. */
/**
 * Reduced motion is not passed as `motion: false`: the first time that token
 * turns off, ConfigProvider wraps its children in a new provider and React
 * remounts the whole app (open dialogs close). tokens.css removes every
 * animation and transition under [data-reduced-motion="1"] instead, Ant
 * Design's included; `reducedMotion` only shortens the durations it computes.
 */
export const buildAntdTheme = (
  scheme: ColorScheme,
  reducedMotion: boolean,
): ThemeConfig => {
  const p = palettes[scheme];
  return {
    algorithm:
      scheme === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    token: {
      colorPrimary: p.acc,
      colorInfo: p.acc,
      colorLink: p['acc-ink'],
      colorLinkHover: p.acc,
      colorError: p.danger,
      colorSuccess: p.success,
      colorWarning: p.warning,
      colorText: p.text,
      colorTextSecondary: p['text-2'],
      colorTextTertiary: p['text-3'],
      colorTextPlaceholder: p['text-3'],
      colorTextDisabled: p['dis-fg'],
      colorBgContainerDisabled: p['dis-bg'],
      colorBgBase: p.surface,
      colorBgContainer: p.surface,
      colorBgElevated: p['shell-fill'],
      colorBgLayout: p.canvas,
      // Kit: an opaque surround replaces the page behind a dialog.
      colorBgMask: p.surround,
      colorBorder: p.boundary,
      colorBorderSecondary: p.edge,
      colorSplit: p.edge,
      controlItemBgHover: p.hover,
      controlItemBgActive: p['acc-soft'],
      fontFamily: 'var(--font-ui)',
      // 14 px Ant Design base, 16 px app base; font sizes become rem
      // (typeRem.ts), control sizes stay in px.
      fontSize: 14,
      borderRadius: 12,
      borderRadiusSM: 10,
      borderRadiusLG: 18,
      controlHeight: 36,
      controlHeightLG: 44,
      // Header 30 and drawer 40 (tokens.css) stay below every popup.
      zIndexPopupBase: 1000,
      ...(reducedMotion
        ? {
            motionDurationFast: '0s',
            motionDurationMid: '0s',
            motionDurationSlow: '0s',
          }
        : {}),
    },
    components: {
      // Kit Button: weight 600, no shadow, 12 px secondary / default shells
      // (the primary pill is in globals.css), 36 px controls and 44 px large
      // ones. Small buttons are 36 px as well: nothing a finger touches is
      // shorter (spec §2), and globals.css widens their hit area to 44 px.
      Button: {
        fontWeight: 600,
        defaultShadow: 'none',
        primaryShadow: 'none',
        dangerShadow: 'none',
        borderRadius: 12,
        borderRadiusLG: 12,
        borderRadiusSM: 12,
        controlHeight: 36,
        controlHeightLG: 44,
        controlHeightSM: 36,
        paddingInline: 16,
        paddingInlineLG: 20,
        paddingInlineSM: 12,
        contentFontSize: 14,
        contentFontSizeLG: 15,
        contentFontSizeSM: 14,
        contentLineHeight: 1.2,
        contentLineHeightLG: 1.2,
        contentLineHeightSM: 1.2,
        defaultColor: p.text,
        defaultBg: p.surface,
        defaultBorderColor: p.boundary,
        // Kit hover: the shell fills with --hover, the border stays.
        defaultHoverBg: p.hover,
        defaultHoverColor: p.text,
        defaultHoverBorderColor: p.boundary,
        defaultActiveBg: p.hover,
        defaultActiveColor: p.text,
        defaultActiveBorderColor: p.boundary,
        colorPrimary: p.acc,
        colorPrimaryHover: p['acc-hover'],
        colorPrimaryActive: p['acc-hover'],
        primaryColor: p['on-acc'],
        colorLink: p['acc-ink'],
        colorLinkHover: p.acc,
      },
      // Kit TextField / Select: 44 px, 16 px text, 10 px radius at every
      // text size (16 px also stops iOS zooming into a focused field).
      Input: {
        borderRadius: 10,
        borderRadiusLG: 10,
        borderRadiusSM: 10,
        controlHeight: 44,
        inputFontSize: 16,
        paddingBlock: 0,
        paddingInline: 12,
        colorBorder: p.boundary,
      },
      InputNumber: { borderRadius: 10, controlHeight: 44, inputFontSize: 16 },
      Select: {
        borderRadius: 10,
        borderRadiusLG: 10,
        borderRadiusSM: 10,
        controlHeight: 44,
        fontSize: 16,
        optionFontSize: 15,
        optionHeight: 44,
        colorBorder: p.boundary,
      },
      Modal: {
        titleFontSize: 20,
        titleLineHeight: 1.4,
        contentBg: p['shell-fill'],
        headerBg: p['shell-fill'],
        footerBg: p['shell-fill'],
      },
      // Kit FolderTree: 44 px rows (a touch target each), 10 px radius,
      // 15 px labels, the selected row in --acc-soft.
      Tree: {
        titleHeight: 44,
        borderRadius: 10,
        fontSize: 15,
        nodeSelectedBg: p['acc-soft'],
        nodeHoverBg: p.hover,
      },
      // Kit DataTable: header on --surface (not antd's grey), 6 / 12 cell
      // padding at every size, square corners on the header row.
      Table: {
        headerBg: p.surface,
        headerColor: p['text-2'],
        headerSortActiveBg: p.surface,
        headerSortHoverBg: p.hover,
        headerSplitColor: 'transparent',
        headerBorderRadius: 0,
        bodySortBg: 'transparent',
        rowHoverBg: p.hover,
        borderColor: p.edge,
        cellPaddingBlock: 6,
        cellPaddingInline: 12,
        cellPaddingBlockMD: 6,
        cellPaddingInlineMD: 12,
        cellPaddingBlockSM: 6,
        cellPaddingInlineSM: 12,
      },
      // Kit Checkbox: 20 px squares with a 5 px radius.
      Checkbox: { borderRadiusSM: 5, controlInteractiveSize: 20 },
      // Kit Menu: 44 px items, 0 12 padding, 15 px / 500 labels, 10 px item
      // radius inside an 18 px frame.
      Dropdown: {
        fontSize: 15,
        paddingBlock: 10,
        controlPaddingHorizontal: 12,
        borderRadiusLG: 18,
        borderRadiusSM: 10,
        controlItemBgHover: p.hover,
      },
      Tooltip: {
        colorBgSpotlight: p['tip-bg'],
        colorTextLightSolid: p['tip-fg'],
      },
    },
  };
};
